import { Fragment, useContext, useMemo } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { useSelect, useDispatch } from '@wordpress/data';
import { Stack, Text } from '@wordpress/ui';
import {
	store as blockEditorStore,
	privateApis as blockEditorPrivateApis,
} from '@wordpress/block-editor';
import { unlock } from '../../lock-unlock';
import { NoteThread } from './note-thread';
import { focusNoteThread } from './utils';
import {
	NoteDraftsContext,
	useFloatingBoard,
	useNoteActions,
	useNoteFocus,
	usePickNote,
} from './hooks';
import { AddNote } from './add-note';
import { store as editorStore } from '../../store';

const { useBlockElement } = unlock( blockEditorPrivateApis );

export function Notes( { notes, sidebarRef, isFloating = false } ) {
	const {
		onCreate: onAddReply,
		onEdit: onEditNote,
		onDiscard,
		onDelete,
	} = useNoteActions();
	const { selectNote } = unlock( useDispatch( editorStore ) );
	const { toggleBlockHighlight } = unlock( useDispatch( blockEditorStore ) );
	const drafts = useContext( NoteDraftsContext );
	const pickNote = usePickNote( { drafts, onDiscard } );

	const { selectedBlockClientId, orderedBlockIds } = useSelect(
		( select ) => {
			const { getSelectedBlockClientId, getClientIdsWithDescendants } =
				select( blockEditorStore );
			return {
				selectedBlockClientId: getSelectedBlockClientId(),
				orderedBlockIds: getClientIdsWithDescendants(),
			};
		},
		[]
	);
	const selectedNote = useSelect(
		( select ) => unlock( select( editorStore ) ).getSelectedNote(),
		[]
	);

	const relatedBlockElement = useBlockElement( selectedBlockClientId );

	const threads = useMemo( () => {
		// In floating mode with a pending new note, splice a placeholder
		// entry at the selected block's position so the board can float it
		// alongside regular threads.
		if ( ! isFloating || selectedNote !== 'new' ) {
			return notes;
		}
		const newNoteThread = {
			id: 'new',
			blockClientId: selectedBlockClientId,
			content: { rendered: '' },
		};
		const out = [];
		orderedBlockIds.forEach( ( blockId ) => {
			// Blocks can carry multiple notes — surface them all.
			const threadsForBlock = notes.filter(
				( t ) => t.blockClientId === blockId
			);
			out.push( ...threadsForBlock );
			if ( blockId === selectedBlockClientId ) {
				// Place the new note placeholder after the block's existing
				// threads so the form appears alongside them.
				out.push( newNoteThread );
			}
		} );
		return out;
	}, [
		notes,
		isFloating,
		selectedNote,
		selectedBlockClientId,
		orderedBlockIds,
	] );

	const handleDelete = async ( note ) => {
		const currentIndex = threads.findIndex( ( t ) => t.id === note.id );
		const nextThread = threads[ currentIndex + 1 ];
		const prevThread = threads[ currentIndex - 1 ];

		const deleted = await onDelete( note );
		// Leave the selection alone when the delete failed; the note is still there.
		if ( ! deleted ) {
			return;
		}

		if ( note.parent !== 0 ) {
			// Move focus to the parent thread when a reply was deleted.
			selectNote( note.parent );
			focusNoteThread( note.parent, sidebarRef.current );
			return;
		}

		const adjacentThread = nextThread ?? prevThread;
		if ( adjacentThread ) {
			pickNote( adjacentThread.id, adjacentThread.blockClientId );
			focusNoteThread( adjacentThread.id, sidebarRef.current );
		} else {
			selectNote( undefined );
			toggleBlockHighlight( note.blockClientId, false );
			// Move focus to the related block.
			relatedBlockElement?.focus();
		}
	};

	const { notePositions, heights, registerThread, unregisterThread } =
		useFloatingBoard( {
			threads,
			selectedNoteId: selectedNote,
			isFloating,
			sidebarRef,
		} );
	useNoteFocus( {
		sidebarRef,
		isPlaced: ! isFloating || notePositions[ selectedNote ] !== undefined,
	} );

	const navigate = ( event, thread, isSelected ) => {
		if ( event.defaultPrevented ) {
			return;
		}

		const currentIndex = threads.findIndex( ( t ) => t.id === thread.id );
		const isSelfTarget = event.currentTarget === event.target;

		if (
			( event.key === 'Enter' || event.key === 'ArrowRight' ) &&
			isSelfTarget &&
			! isSelected
		) {
			// Expand thread.
			pickNote( thread.id, thread.blockClientId );
		} else if (
			( ( event.key === 'Enter' || event.key === 'ArrowLeft' ) &&
				isSelfTarget &&
				isSelected ) ||
			event.key === 'Escape'
		) {
			// Collapse thread.
			selectNote( undefined );
			if ( thread.blockClientId ) {
				toggleBlockHighlight( thread.blockClientId, false );
			}
			focusNoteThread( thread.id, sidebarRef.current );
		} else if ( isSelfTarget ) {
			const targetIndex = {
				ArrowDown: Math.min( currentIndex + 1, threads.length - 1 ),
				ArrowUp: Math.max( currentIndex - 1, 0 ),
				Home: 0,
				End: threads.length - 1,
			}[ event.key ];
			if ( targetIndex !== undefined ) {
				// The floating panel scrolls; keep the key from scrolling it too.
				event.preventDefault();
				focusNoteThread(
					threads[ targetIndex ].id,
					sidebarRef.current
				);
			}
		}
	};

	// In the "All notes" view, find where the resolved notes begin so a
	// "Resolved" divider can be rendered above them. Resolved notes (status
	// 'approved' with a still-present block) always sort after the active
	// ones, so the first match marks the boundary. The floating view only
	// lists unresolved notes, so it needs no divider.
	const firstResolvedIndex = isFloating
		? -1
		: threads.findIndex(
				( thread ) =>
					thread.status === 'approved' && !! thread.blockClientId
			);
	const isAddingNote =
		! isFloating && selectedNote === 'new' && !! selectedBlockClientId;

	return (
		<Stack
			className="editor-collab-sidebar-panel"
			role="tree"
			direction="column"
			gap="md"
			justify="flex-start"
			ref={ ( node ) => {
				// Sometimes previous sidebar unmounts after the new one mounts.
				// This ensures we always have the latest reference.
				if ( node ) {
					sidebarRef.current = node;
				}
			} }
			aria-label={
				isFloating ? __( 'Unresolved notes' ) : __( 'All notes' )
			}
		>
			{ isAddingNote && (
				<AddNote
					key={ selectedBlockClientId }
					clientId={ selectedBlockClientId }
					onSubmit={ onAddReply }
					onDiscard={ onDiscard }
					sidebarRef={ sidebarRef }
				/>
			) }
			{ threads.map( ( thread, index ) => (
				<Fragment key={ thread.id }>
					{ index === firstResolvedIndex && (
						<Stack
							direction="row"
							align="center"
							justify="center"
							gap="sm"
							className="editor-collab-sidebar-panel__status-separator"
						>
							<Text variant="heading-sm" render={ <p /> }>
								{ __( 'Resolved' ) }
							</Text>
						</Stack>
					) }
					<NoteThread
						note={ thread }
						onAddReply={ onAddReply }
						onDiscard={ onDiscard }
						onDeleteNote={ handleDelete }
						onEditNote={ onEditNote }
						isSelected={ selectedNote === thread.id }
						sidebarRef={ sidebarRef }
						floating={
							isFloating
								? {
										y: notePositions[ thread.id ],
										height: heights[ thread.id ],
										registerThread,
										unregisterThread,
									}
								: undefined
						}
						onKeyDown={ ( event ) =>
							navigate(
								event,
								thread,
								selectedNote === thread.id
							)
						}
					/>
				</Fragment>
			) ) }
		</Stack>
	);
}
